import { fromTable, supabase } from "@/integrations/supabase/db";
import { extractOriginalFileName } from "./fileNameUtils";

interface DocumentMetadata {
  id: string;
  storage_url: string;
  display_name: string;
  created_at: string;
  updated_at: string;
}

/**
 * Gets the display name for a document URL.
 * First checks the database for a custom display name, then falls back to extracting from URL.
 * @param storageUrl - The storage URL of the document
 * @returns The display name for the document
 */
export async function getDisplayName(storageUrl: string): Promise<string> {
  try {
    // Normalize URL for consistent lookup
    const normalizedUrl = normalizeStorageUrl(storageUrl);
    
    const { data, error } = await supabase
      .from('dokument_metadaten')
      .select('display_name')
      .eq('storage_url', normalizedUrl)
      .maybeSingle();
    
    if (error) {
      console.error('Error fetching document metadata:', error);
      return extractOriginalFileName(storageUrl) || 'Dokument';
    }
    
    if (data?.display_name) {
      return data.display_name;
    }
    
    // Fallback: extract from URL
    return extractOriginalFileName(storageUrl) || 'Dokument';
  } catch (error) {
    console.error('Error in getDisplayName:', error);
    return extractOriginalFileName(storageUrl) || 'Dokument';
  }
}

/**
 * Sets or updates the display name for a document URL.
 * This is instant and doesn't require file operations.
 * @param storageUrl - The storage URL of the document
 * @param displayName - The new display name to set
 * @returns Success status and error message if failed
 */
export async function setDisplayName(
  storageUrl: string,
  displayName: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Normalize URL for consistent storage
    const normalizedUrl = normalizeStorageUrl(storageUrl);
    
    // Upsert the metadata (insert or update if exists)
    const { error } = await supabase
      .from('dokument_metadaten')
      .upsert(
        {
          storage_url: normalizedUrl,
          display_name: displayName.trim(),
        },
        {
          onConflict: 'storage_url',
        }
      );
    
    if (error) {
      console.error('Error setting document metadata:', error);
      return { success: false, error: 'Fehler beim Speichern des Namens' };
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error in setDisplayName:', error);
    return { success: false, error: 'Unerwarteter Fehler' };
  }
}

/**
 * Batch fetch display names for multiple URLs.
 * More efficient than calling getDisplayName for each URL individually.
 * @param storageUrls - Array of storage URLs
 * @returns Map of storage URL to display name
 */
export async function getDisplayNamesForUrls(
  storageUrls: string[]
): Promise<Map<string, string>> {
  const result = new Map<string, string>();
  
  if (storageUrls.length === 0) {
    return result;
  }
  
  try {
    // Normalize all URLs
    const normalizedUrls = storageUrls.map(normalizeStorageUrl);
    
    const { data, error } = await supabase
      .from('dokument_metadaten')
      .select('storage_url, display_name')
      .in('storage_url', normalizedUrls);
    
    if (error) {
      console.error('Error fetching batch document metadata:', error);
    }
    
    // Create map from database results
    const dbMap = new Map<string, string>();
    if (data) {
      data.forEach((item) => {
        dbMap.set(item.storage_url, item.display_name);
      });
    }
    
    // Build result map with fallbacks
    storageUrls.forEach((url) => {
      const normalizedUrl = normalizeStorageUrl(url);
      const displayName = dbMap.get(normalizedUrl) || extractOriginalFileName(url) || 'Dokument';
      result.set(url, displayName);
    });
    
    return result;
  } catch (error) {
    console.error('Error in getDisplayNamesForUrls:', error);
    
    // Fallback: use URL extraction for all
    storageUrls.forEach((url) => {
      result.set(url, extractOriginalFileName(url) || 'Dokument');
    });
    
    return result;
  }
}

/**
 * Deletes the metadata for a document URL.
 * Should be called when the actual file is deleted.
 * @param storageUrl - The storage URL of the document
 */
export async function deleteDisplayName(storageUrl: string): Promise<void> {
  try {
    const normalizedUrl = normalizeStorageUrl(storageUrl);
    
    await supabase
      .from('dokument_metadaten')
      .delete()
      .eq('storage_url', normalizedUrl);
  } catch (error) {
    console.error('Error deleting document metadata:', error);
  }
}

/**
 * Normalizes a storage URL for consistent database lookups.
 * Removes query parameters and ensures consistent format.
 */
function normalizeStorageUrl(url: string): string {
  // Remove query parameters
  const urlWithoutParams = url.split('?')[0];
  return urlWithoutParams;
}

/**
 * Custom hook helper to get display name with caching.
 * Can be used with React Query for efficient data fetching.
 */
export function createDisplayNameQueryKey(storageUrl: string): string[] {
  return ['document-display-name', normalizeStorageUrl(storageUrl)];
}

// Supabase project ref for URL construction
const SUPABASE_PROJECT_REF = "hwjnhvbrkvzumzhakgla";
const SUPABASE_URL = `https://${SUPABASE_PROJECT_REF}.supabase.co`;

/**
 * Converts a relative path to a full public Supabase storage URL.
 */
export function resolveToFullUrl(urlOrPath: string, bucket: string = 'contract-documents'): string {
  if (!urlOrPath) return '';
  
  // Already a full URL
  if (urlOrPath.startsWith('http://') || urlOrPath.startsWith('https://')) {
    return urlOrPath;
  }
  
  // Relative path - construct full URL
  return `${SUPABASE_URL}/storage/v1/object/public/${bucket}/${urlOrPath}`;
}

/**
 * Extracts bucket and path from a full Supabase storage URL.
 * @param fullUrl - Full Supabase storage URL
 * @returns Object with bucket and path, or null if not a valid storage URL
 */
export function extractBucketAndPath(fullUrl: string): { bucket: string; path: string } | null {
  try {
    // Pattern: https://xxx.supabase.co/storage/v1/object/public/BUCKET/PATH
    const storagePattern = /\/storage\/v1\/object\/public\/([^\/]+)\/(.+)$/;
    const match = fullUrl.match(storagePattern);
    
    if (match) {
      return {
        bucket: match[1],
        path: decodeURIComponent(match[2])
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error extracting bucket and path:', error);
    return null;
  }
}

/**
 * Constructs the Edge Function URL for serving a document with correct filename.
 * @param bucket - Storage bucket name
 * @param path - File path within the bucket
 * @returns Edge Function URL that serves the file with correct Content-Disposition header
 */
export function getServeDocumentUrl(bucket: string, path: string): string {
  const encodedPath = encodeURIComponent(path);
  return `${SUPABASE_URL}/functions/v1/serve-document?bucket=${bucket}&path=${encodedPath}`;
}

/**
 * Gets the display name for download - async function for use in event handlers.
 * First checks database, then falls back to extracting from URL.
 * @param urlOrPath - Storage URL or relative path
 * @param bucket - Storage bucket name (default: contract-documents)
 * @returns Display name for the file
 */
export async function getDisplayNameForDownload(
  urlOrPath: string,
  bucket: string = 'contract-documents'
): Promise<string> {
  try {
    const fullUrl = resolveToFullUrl(urlOrPath, bucket);
    return await getDisplayName(fullUrl);
  } catch (error) {
    console.error('Error getting display name for download:', error);
    return extractOriginalFileName(urlOrPath) || 'Dokument';
  }
}

/**
 * Opens a file in a new browser tab with the correct display name.
 * Uses Edge Function proxy that serves the file with correct Content-Disposition header.
 * This ensures the correct filename is used for ALL download methods (native PDF viewer, save as, etc.)
 * @param urlOrPath - Storage URL or relative path
 * @param bucket - Storage bucket name (default: contract-documents)
 */
export async function openFileInNewTab(
  urlOrPath: string,
  bucket: string = 'contract-documents'
): Promise<void> {
  try {
    // Determine bucket and path
    let resolvedBucket = bucket;
    let resolvedPath = urlOrPath;
    
    // If it's a full URL, extract bucket and path
    if (urlOrPath.startsWith('http://') || urlOrPath.startsWith('https://')) {
      const extracted = extractBucketAndPath(urlOrPath);
      if (extracted) {
        resolvedBucket = extracted.bucket;
        resolvedPath = extracted.path;
      } else {
        // Not a Supabase storage URL - open directly (e.g., external links)
        window.open(urlOrPath, '_blank');
        return;
      }
    }
    
    // Use Edge Function URL which serves the file with correct Content-Disposition header
    const serveUrl = getServeDocumentUrl(resolvedBucket, resolvedPath);
    
    console.log('Opening file via Edge Function:', serveUrl);
    
    // Get auth token for authorized access
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.access_token) {
      console.warn('No auth session, falling back to direct URL');
      const fullUrl = resolveToFullUrl(urlOrPath, bucket);
      window.open(fullUrl, '_blank');
      return;
    }
    
    // Fetch the file with authorization
    const response = await fetch(serveUrl, {
      headers: {
        'Authorization': `Bearer ${session.access_token}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${response.status}`);
    }
    
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    
    // Open in new tab
    window.open(blobUrl, '_blank');
    
    // Clean up blob URL after a delay
    setTimeout(() => {
      URL.revokeObjectURL(blobUrl);
    }, 60000); // Keep for 1 minute for viewing
  } catch (error) {
    console.error('Error opening file in new tab:', error);
    // Fallback to direct open
    const fullUrl = resolveToFullUrl(urlOrPath, bucket);
    window.open(fullUrl, '_blank');
  }
}

/**
 * Constructs the Edge Function URL for downloading a document with correct filename.
 * @param bucket - Storage bucket name
 * @param path - File path within the bucket
 * @returns Edge Function URL that serves the file with 'attachment' Content-Disposition header
 */
export function getDownloadDocumentUrl(bucket: string, path: string): string {
  const encodedPath = encodeURIComponent(path);
  return `${SUPABASE_URL}/functions/v1/serve-document?bucket=${bucket}&path=${encodedPath}&download=true`;
}

/**
 * Downloads a file using the Edge Function proxy with correct filename.
 * Uses fetch + blob approach to ensure downloads work for all file types.
 * This ensures the downloaded file has the correct display name from metadata.
 * @param urlOrPath - Storage URL or relative path
 * @param bucket - Storage bucket name (default: contract-documents)
 */
export async function downloadFileWithCorrectName(
  urlOrPath: string,
  bucket: string = 'contract-documents'
): Promise<void> {
  try {
    // Determine bucket and path
    let resolvedBucket = bucket;
    let resolvedPath = urlOrPath;
    
    // If it's a full URL, extract bucket and path
    if (urlOrPath.startsWith('http://') || urlOrPath.startsWith('https://')) {
      const extracted = extractBucketAndPath(urlOrPath);
      if (extracted) {
        resolvedBucket = extracted.bucket;
        resolvedPath = extracted.path;
      } else {
        // Not a Supabase storage URL - fallback to direct download
        const link = document.createElement('a');
        link.href = urlOrPath;
        link.download = '';
        link.target = '_blank';
        link.click();
        return;
      }
    }
    
    // Use Edge Function URL with download=true for 'attachment' disposition
    const downloadUrl = getDownloadDocumentUrl(resolvedBucket, resolvedPath);
    
    console.log('Downloading file via Edge Function:', downloadUrl);
    
    // Get auth token for authorized access
    const { data: { session } } = await supabase.auth.getSession();
    
    // Fetch the file as blob with authorization - this works reliably for ALL file types
    const response = await fetch(downloadUrl, {
      headers: session?.access_token ? {
        'Authorization': `Bearer ${session.access_token}`
      } : {}
    });
    
    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.status}`);
    }
    const blob = await response.blob();
    
    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = extractOriginalFileName(urlOrPath) || 'document';
    if (contentDisposition) {
      const match = contentDisposition.match(/filename\*?=['"]?(?:UTF-8'')?([^"';\n]+)/i);
      if (match) {
        filename = decodeURIComponent(match[1]);
      }
    }
    
    // Create object URL and trigger download
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up after a short delay
    setTimeout(() => {
      URL.revokeObjectURL(objectUrl);
    }, 1000);
  } catch (error) {
    console.error('Error downloading file:', error);
    // Fallback to direct download
    const fullUrl = resolveToFullUrl(urlOrPath, bucket);
    const link = document.createElement('a');
    link.href = fullUrl;
    link.download = '';
    link.target = '_blank';
    link.click();
  }
}
